import React, {
  useCallback, useEffect, useState,
} from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
  Alert,
  FormFeedback,
} from 'reactstrap';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import {
  setUserSetting,
  updateUserSettings,
} from '../../actions/Auth';

import { isMini } from '../../helpers/App';
import {
  getCurrentUser,
  userIsAdmin,
  userIsManager,
} from '../../helpers/Auth';
import {
  prefixSuffixLabels,
  prefixSuffixSeparators,
} from '../../helpers/Files';
import {
  getSetting,
  getUserSetting,
  getUserSettings,
} from '../../helpers/Settings';
import { userHasFeature } from '../../helpers/Features';

import HelpIcon from '../HelpIcon';
import UpgradePlanButton from '../UpgradePlanButton';

const FileUploadAddPrefixSuffix = ({
  includePrefix,
  handleIncludePrefix,
  handlePrefixLabel,
  prefixLabel,
  handlePrefixSeparator,
  prefixSeparator,
  includeSuffix,
  handleIncludeSuffix,
  handleSuffixLabel,
  suffixLabel,
  handleSuffixSeparator,
  suffixSeparator,
  prefixOffset,
  handlePrefixOffset,
  suffixOffset,
  handleSuffixOffset,
  ignoreDisabled,
  identifier,
  zIndex,
  handleUserSetting,
}) => {
  const intl = useIntl();

  const [initialized, setInitialized] = useState(false);

  const hasFeature = userHasFeature('file-name-prefix-suffix');

  const settings = getUserSettings();
  const destinationSetting = settings.file_name_prefix_and_suffix_default;

  const [advancedPrefixLabel, setAdvancedPrefixLabel] = useState(false);
  const [advancedSuffixLabel, setAdvancedSuffixLabel] = useState(false);
  const [destination, setDestination] = useState(destinationSetting);
  const [custom, setCustom] = useState({
    prefix: {
      label: '', separator: '', include: false,
    },
    suffix: {
      label: '', separator: '', include: false,
    },
  });

  const forceWorkspaceSetting = identifier === 'upload-modal' && getSetting('file_name_prefix_and_suffix_force_workspace');

  const labels = prefixSuffixLabels;
  const separators = prefixSuffixSeparators;

  const handleCustomValues = (key, subKey, value) => {
    setCustom((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [subKey]: value,
      },
    }));
    setDestination('custom');
  };

  const handleValues = useCallback((destinationValue = destination, isInitialized = true) => {
    let setting;

    const workspaceSetting = getSetting('file_name_prefix_and_suffix');
    const userSetting = getUserSetting('file_name_prefix_and_suffix');

    if (identifier === 'admin-settings') {
      setting = workspaceSetting;
    } else if (identifier === 'user-settings') {
      setting = userSetting;
    } else if (forceWorkspaceSetting || destinationValue === 'workspace') {
      setting = workspaceSetting;
    } else {
      setting = userSetting;
    }

    handlePrefixLabel(setting.prefix.label, initialized);
    handleSuffixLabel(setting.suffix.label, initialized);
    handlePrefixSeparator(setting.prefix.separator, initialized);
    handleSuffixSeparator(setting.suffix.separator, initialized);
    handleIncludePrefix(setting.prefix.include, initialized);
    handleIncludeSuffix(setting.suffix.include, initialized);

    if (!isInitialized) {
      setCustom({
        prefix: {
          label: setting.prefix.label,
          separator: setting.prefix.separator,
          include: setting.prefix.include,
        },
        suffix: {
          label: setting.suffix.label,
          separator: setting.suffix.separator,
          include: setting.suffix.include,
        },
      });
    }
  }, [
    destination,
    forceWorkspaceSetting,
    handleIncludePrefix,
    handleIncludeSuffix,
    handlePrefixLabel,
    handlePrefixSeparator,
    handleSuffixLabel,
    handleSuffixSeparator,
    identifier,
    initialized,
  ]);

  const handleApplyCustom = () => {
    const newPrefixLabel = labels.find(label => label.value === custom.prefix.label);
    const newSuffixLabel = labels.find(label => label.value === custom.suffix.label);
    const newPrefixSeparator = separators.find(sep => sep.value === custom.prefix.separator);
    const newSuffixSeparator = separators.find(sep => sep.value === custom.suffix.separator);

    handlePrefixLabel(newPrefixLabel.value);
    handleSuffixLabel(newSuffixLabel.value);
    handlePrefixSeparator(newPrefixSeparator.value);
    handleSuffixSeparator(newSuffixSeparator.value);
    handleIncludePrefix(custom.prefix.include);
    handleIncludeSuffix(custom.suffix.include);
    if (prefixOffset) {
      setAdvancedPrefixLabel(true);
    }
    if (suffixOffset) {
      setAdvancedSuffixLabel(true);
    }
  };

  const handleChangeDestination = async (e) => {
    const { value } = e.target;

    setDestination(value);

    if (destination !== 'custom' && value === 'custom') {
      handleApplyCustom();
    }

    if (value !== 'user' && value !== 'workspace') return;

    handleUserSetting({ file_name_prefix_and_suffix_default: value });
    handleValues(value);
  };

  const validateOffset = (str) => (
    !(
      str === '' ||
      /^[a-zA-Z]+$/.test(str) ||
      /^\d+$/.test(str)
    )
  );

  useEffect(() => {
    if (initialized) return;
    if (!hasFeature) {
      setInitialized(true);
      return;
    }

    if (identifier !== 'upload-modal' ||
      (!prefixLabel && !suffixLabel && !prefixSeparator && !suffixSeparator)
    ) {
      handleValues(destination, false);
      setInitialized(true);
    }
  }, [
    hasFeature,
    handlePrefixLabel,
    handlePrefixSeparator,
    handleSuffixLabel,
    handleSuffixSeparator,
    labels,
    prefixLabel,
    prefixSeparator,
    separators,
    suffixLabel,
    suffixSeparator,
    initialized,
    handleIncludePrefix,
    handleIncludeSuffix,
    forceWorkspaceSetting,
    destination,
    identifier,
    handleValues,
  ]);

  useEffect(() => {
    if (!includePrefix && advancedPrefixLabel) setAdvancedPrefixLabel(false);
    if (!includeSuffix && advancedSuffixLabel) setAdvancedSuffixLabel(false);
  }, [advancedPrefixLabel, advancedSuffixLabel, includePrefix, includeSuffix]);

  return (
    <>
      <FormGroup>
        {identifier === 'upload-modal' && (
          <Row>
            <Col className="uploaded-files-content-advanced-header-column">
              <h5>
                {intl.formatMessage({ defaultMessage: 'Enable file name prefix and/or suffix' })}
              </h5>
              <UpgradePlanButton feature="file-name-prefix-suffix" />
            </Col>
          </Row>
        )}
        {hasFeature && forceWorkspaceSetting && (
          <Alert color="light" className="mt-4">
            {intl.formatMessage({ defaultMessage: 'File prefix and suffix are set by your workspace owner and cannot be changed.' })}
          </Alert>
        )}
        <Label
          for={`includePrefix-${identifier}`}
          className={classnames('switch', { disabled: forceWorkspaceSetting || !hasFeature })}
          check
        >
          <div className="switch-details">
            <span>{intl.formatMessage({ defaultMessage: 'Add file name prefix' })}</span>
          </div>
          <div className="switch-control input-inner">
            <Input
              type="checkbox"
              id={`includePrefix-${identifier}`}
              name={`includePrefix-${identifier}`}
              checked={includePrefix && hasFeature}
              value={true}
              onChange={(e) => {
                const isChecked = e.target.checked;
                handleIncludePrefix(isChecked);
                handleCustomValues('prefix', 'include', isChecked);
              }}
              disabled={forceWorkspaceSetting || !hasFeature}
            />
            <i />
          </div>
        </Label>
        {includePrefix && (
          <Row>
            <Col className="prefix-suffix-column">
              <Label className="sub-label">{intl.formatMessage({ defaultMessage: 'Label' })}</Label>
              <Row>
                <Col className="prefix-suffix-label-column">
                  <Select
                    className="prefix-suffix-select"
                    classNamePrefix="prefix-suffix-select"
                    getOptionValue={option => option.value}
                    getOptionLabel={option => option.label}
                    isDisabled={(!includePrefix && !ignoreDisabled) || forceWorkspaceSetting}
                    options={labels}
                    onChange={(value) => {
                      handlePrefixLabel(value.value);
                      handleCustomValues('prefix', 'label', value.value);
                    }}
                    value={labels.find(label => label.value === prefixLabel)}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex }) }}
                  />
                  {identifier === 'upload-modal' && (
                    <Button
                      color={advancedPrefixLabel ? 'primary' : 'link'}
                      onClick={() => setAdvancedPrefixLabel(!advancedPrefixLabel)}
                      disabled={!includePrefix}
                    >
                      <i className="fal fa-cog mr-0" />
                    </Button>
                  )}
                </Col>
              </Row>
              {advancedPrefixLabel && (
                <>
                  <Label for={`prefixOffset-${identifier}`} className="sub-label mt-3">
                    {intl.formatMessage({ defaultMessage: 'Start your label from' })}
                    <HelpIcon id="prefix-label-tooltip" permanent={true}>
                      {intl.formatMessage({
                        defaultMessage:
                        'Start the sequence of your file prefix from a certain number or letter (i.e. 5, 10, 150 or h, ab, abb)',
                      })}
                    </HelpIcon>
                  </Label>
                  <Input
                    type="text"
                    value={prefixOffset}
                    onChange={(e) => {
                      handlePrefixOffset(e.target.value);
                    }}
                    id={`prefixOffset-${identifier}`}
                    placeholder={prefixLabel === '1' ? '1, 2, 10, 11, 150...' : 'a, b, ab, A, B, ABCD...'}
                  />
                  {validateOffset(prefixOffset) && (
                    <FormFeedback style={{ display: 'block' }}>
                      {intl.formatMessage({ defaultMessage: 'Please provide only numbers or letters' })}
                    </FormFeedback>
                  )}
                </>
              )}
            </Col>
            <Col className="prefix-suffix-column">
              <Label className="sub-label">{intl.formatMessage({ defaultMessage: 'Separator' })}</Label>
              <Select
                className="prefix-suffix-select"
                classNamePrefix="prefix-suffix-select"
                getOptionValue={option => option.value}
                getOptionLabel={option => option.label}
                isDisabled={(!includePrefix && !ignoreDisabled) || forceWorkspaceSetting}
                options={separators}
                onChange={(value) => {
                  handlePrefixSeparator(value.value);
                  handleCustomValues('prefix', 'separator', value.value);
                }}
                value={separators.find(sep => sep.value === prefixSeparator)}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex }) }}
              />
            </Col>
          </Row>
        )}
        <Label
          for={`includeSuffix-${identifier}`}
          className={classnames('switch mt-3', { disabled: forceWorkspaceSetting || !hasFeature })}
        >
          <div className="switch-details">
            <span>{intl.formatMessage({ defaultMessage: 'Add file name suffix' })}</span>
          </div>
          <div className="switch-control input-inner">
            <Input
              type="checkbox"
              id={`includeSuffix-${identifier}`}
              name={`includeSuffix-${identifier}`}
              checked={includeSuffix && hasFeature}
              value={true}
              onChange={(e) => {
                const isChecked = e.target.checked;
                handleIncludeSuffix(isChecked);
                handleCustomValues('suffix', 'include', isChecked);
              }}
              disabled={forceWorkspaceSetting || !hasFeature}
            />
            <i />
          </div>
        </Label>
        {includeSuffix && (
          <Row>
            <Col className="prefix-suffix-column">
              <Label className="sub-label">{intl.formatMessage({ defaultMessage: 'Separator' })}</Label>
              <Select
                className="prefix-suffix-select"
                classNamePrefix="prefix-suffix-select"
                getOptionValue={option => option.value}
                getOptionLabel={option => option.label}
                isDisabled={(!includeSuffix && !ignoreDisabled) || forceWorkspaceSetting}
                options={separators}
                onChange={(value) => {
                  handleSuffixSeparator(value.value);
                  handleCustomValues('suffix', 'separator', value.value);
                }}
                value={separators.find(sep => sep.value === suffixSeparator)}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex }) }}
              />
            </Col>
            <Col className="prefix-suffix-column">
              <Label className="sub-label">{intl.formatMessage({ defaultMessage: 'Label' })}</Label>
              <Row>
                <Col className="prefix-suffix-label-column">
                  <Select
                    className="prefix-suffix-select"
                    classNamePrefix="prefix-suffix-select"
                    getOptionValue={option => option.value}
                    getOptionLabel={option => option.label}
                    isDisabled={(!includeSuffix && !ignoreDisabled) || forceWorkspaceSetting}
                    options={labels}
                    onChange={(value) => {
                      handleSuffixLabel(value.value);
                      handleCustomValues('suffix', 'label', value.value);
                    }}
                    value={labels.find(label => label.value === suffixLabel)}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex }) }}
                    disabled={forceWorkspaceSetting}
                  />
                  {identifier === 'upload-modal' && (
                    <Button
                      color={advancedSuffixLabel ? 'primary' : 'link'}
                      onClick={() => setAdvancedSuffixLabel(!advancedSuffixLabel)}
                      disabled={!includeSuffix}
                    >
                      <i className="fal fa-cog mr-0" />
                    </Button>
                  )}
                </Col>
              </Row>
              {advancedSuffixLabel && (
                <>
                  <Label for={`suffixOffset-${identifier}`} className="sub-label mt-3">
                    {intl.formatMessage({ defaultMessage: 'Start your label from' })}
                    <HelpIcon id="suffix-label-tooltip" permanent={true}>
                      {intl.formatMessage({
                        defaultMessage:
                        'Start the sequence of your file suffix from a certain number or letter (i.e. 5, 10, 150 or h, ab, abb)',
                      })}
                    </HelpIcon>
                  </Label>
                  <Input
                    type="text"
                    value={suffixOffset}
                    onChange={(e) => {
                      handleSuffixOffset(e.target.value);
                    }}
                    id={`suffixOffset-${identifier}`}
                    placeholder={suffixLabel === '1' ? '1, 2, 10, 11, 150...' : 'a, b, ab, A, B, ABCD...'}
                  />
                  {validateOffset(suffixOffset) && (
                    <FormFeedback style={{ display: 'block' }}>
                      {intl.formatMessage({ defaultMessage: 'Please provide only numbers or letters' })}
                    </FormFeedback>
                  )}
                </>
              )}
            </Col>
          </Row>
        )}
      </FormGroup>
      {hasFeature && (
        <div className="uploaded-files-content-advanced-footer">
          {identifier === 'upload-modal' && (
          <div className="uploaded-files-content-advanced-footer-text">
            <small className="mt-2">
              {(userIsAdmin() || userIsManager()) && (
                intl.formatMessage({ defaultMessage: 'Enable default workspace prefix and suffix in your <link>workspace settings</link>.' }, {
                  link: (msg) => (
                    isMini() ? (
                      <a
                        href={`${window.location.origin}/admin/settings/prefix-and-suffix`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {msg}
                      </a>
                    ) : (
                      <Link to="/admin/settings/prefix-and-suffix" target="_blank">
                        {msg}
                      </Link>
                    )
                  ),
                })
              )}
            </small>
            {!forceWorkspaceSetting && (
              <small>
                {intl.formatMessage({ defaultMessage: 'Manage your personal default setting in your <link>user settings</link>.' }, {
                  link: (msg) => (
                    isMini() ? (
                      <a
                        href={`${window.location.origin}/profile/edit?tab=prefix-and-suffix`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {msg}
                      </a>
                    ) : (
                      <Link to="/profile/edit?tab=prefix-and-suffix" target="_blank">
                        {msg}
                      </Link>
                    )
                  ),
                })}
              </small>
            )}
          </div>
          )}
          {(identifier === 'upload-modal' && !forceWorkspaceSetting) && (
          <div className="uploaded-files-content-advanced-default">
            {intl.formatMessage({ defaultMessage: 'Default' })}
            <Input
              type="select"
              className="custom-select"
              bsSize="sm"
              value={destination}
              onChange={handleChangeDestination}
            >
              <option value="workspace">{intl.formatMessage({ defaultMessage: 'Workspace' })}</option>
              <option value="user">{intl.formatMessage({ defaultMessage: 'User' })}</option>
              <option value="custom">{intl.formatMessage({ defaultMessage: 'Custom' })}</option>
            </Input>
          </div>
          )}
        </div>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  handleUserSetting: (setting) => {
    const profile = getCurrentUser();
    if (profile) {
      dispatch(setUserSetting(setting));
      dispatch(updateUserSettings(profile.uuid, setting));
    }
  },
});

FileUploadAddPrefixSuffix.propTypes = {
  includePrefix: PropTypes.bool,
  handleIncludePrefix: PropTypes.func,
  handlePrefixLabel: PropTypes.func.isRequired,
  prefixLabel: PropTypes.string,
  handlePrefixSeparator: PropTypes.func.isRequired,
  prefixSeparator: PropTypes.string,
  includeSuffix: PropTypes.bool,
  handleIncludeSuffix: PropTypes.func,
  handleSuffixLabel: PropTypes.func.isRequired,
  suffixLabel: PropTypes.string,
  handleSuffixSeparator: PropTypes.func.isRequired,
  suffixSeparator: PropTypes.string,
  ignoreDisabled: PropTypes.bool,
  identifier: PropTypes.string,
  zIndex: PropTypes.number,
  prefixOffset: PropTypes.any,
  suffixOffset: PropTypes.any,
  handlePrefixOffset: PropTypes.func,
  handleSuffixOffset: PropTypes.func,
  handleUserSetting: PropTypes.func.isRequired,
};

FileUploadAddPrefixSuffix.defaultProps = {
  prefixLabel: null,
  prefixSeparator: null,
  suffixLabel: null,
  suffixSeparator: null,
  includePrefix: false,
  includeSuffix: false,
  ignoreDisabled: false,
  identifier: 'default',
  zIndex: 99,
  prefixOffset: '',
  suffixOffset: '',
  handlePrefixOffset: () => {},
  handleSuffixOffset: () => {},
  handleIncludePrefix: () => {},
  handleIncludeSuffix: () => {},
};

export default connect(null, mapDispatchToProps)(FileUploadAddPrefixSuffix);
